import { Row, Col, Form, Select, message, Button, Typography, Alert } from 'antd'
import { FileTextOutlined } from '@ant-design/icons'
import { useHideModal } from 'components/Modals'
import { useLocalization } from 'localization'
import { useState } from 'react'
import timezones from 'timezones-list'
import { useTheme } from 'styled-components'
import DropZone from 'components/DropZone'
import { RcFile, UploadFile } from 'antd/lib/upload'
import { useTransactionCSVImport } from 'api'
import { useS3Upload } from 'api/aws/mutations'
import Container from 'components/Container'
import { useRouter } from 'next/router'
import appRoutes from 'modules/common/app-routes'
import { buildAppUrl } from 'utils/string'

const timezoneOptions = timezones.map(({ label, tzCode }) => ({
	label,
	value: tzCode,
}))

const clientsTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone

const CustomCSVUpload = () => {
	const [selectedTimezone, selectTimezone] = useState(
		timezoneOptions.some(({ value }) => value === clientsTimezone) ? clientsTimezone : undefined
	)
	const { t } = useLocalization()
	const hideModal = useHideModal()
	const theme = useTheme()
	const [files, setFiles] = useState<UploadFile[]>([])
	const { mutateAsync: s3Upload, isLoading: isUploadingToS3 } = useS3Upload()
	const { mutateAsync: transactionCSVImport, isLoading: isImporting } = useTransactionCSVImport()
	const router = useRouter()

	const onUpload = async () => {
		const file = files?.[0] as RcFile
		if (!process.env.NEXT_PUBLIC_DEPLOYMENT_ENV || !file) {
			message.error(t('errors.BAD_REQUEST'))
			return
		}
		const foundTimezone = timezones.find(({ tzCode }) => tzCode === selectedTimezone)
		const separatorIndex = foundTimezone!.utc.indexOf(':')
		const timezoneValue = foundTimezone!.utc.slice(separatorIndex - 3, separatorIndex)

		const s3FileUrl = await s3Upload({
			env: process.env.NEXT_PUBLIC_DEPLOYMENT_ENV,
			templateType: 'general',
			templateSubtype: 'transactions',
			file,
		})

		await transactionCSVImport({
			fileName: file.name,
			fileUri: s3FileUrl,
			contentType: file.type,
			type: 'BUY_SELL',
			zoneOffset: timezoneValue,
		})
		setFiles([])
		hideModal()
		router.push(appRoutes.integrations.list())
	}

	return (
		<>
			<Container space="md" spaceDirection="vertical">
				<Alert
					message={
						<>
							<Typography.Paragraph>
								1. {t('transactions.csvUploadModal.step1')}{' '}
								<Typography.Link underline href={buildAppUrl('/ManualCSVTemplate.csv')} target="_blank">
									{t('transactions.csvUploadModal.templateDownloadLinkText')}
									<Container display="inline-block" margin={{ left: 'xs' }}>
										<FileTextOutlined />
									</Container>
								</Typography.Link>
								.
							</Typography.Paragraph>
							<Typography.Paragraph italic> {t('transactions.csvUploadModal.step1After')}</Typography.Paragraph>
							<Typography.Text>2. {t('transactions.csvUploadModal.step2')}</Typography.Text>
						</>
					}
					type="info"
				/>
				<Form layout="vertical">
					<Row gutter={[theme.margins.md, 0]}>
						<Col xs={24} sm={12}>
							<Form.Item label={t('settings.personal.timezone')}>
								<Select
									value={selectedTimezone}
									onChange={selectTimezone}
									placeholder={t('placeholders.timezone')}
									options={timezoneOptions}
									data-testid="timezone"
								/>
							</Form.Item>
						</Col>
					</Row>
				</Form>
			</Container>
			<Container display="flex" flexDirection="column" space="md" spaceDirection="vertical">
				<DropZone files={files} setFiles={setFiles} />
				<Container>
					<Button
						type="primary"
						onClick={onUpload}
						disabled={files.length === 0 || !selectedTimezone}
						loading={isImporting || isUploadingToS3}
					>
						{t('actions.upload')}
					</Button>
				</Container>
			</Container>
		</>
	)
}

export default CustomCSVUpload
